import React, { useEffect, useMemo, useState } from "react";
import { BadgeIndianRupee, Boxes, PackageCheck, PackageX, Star, Tags } from "lucide-react";
import { ReportsService } from "../../../services/reports.service";
import { ReportCard, ReportPanel } from "./components/ReportCards";
import DataTable from "./components/DataTable";
import TrendBars from "./components/TrendBars";
import { money, n } from "./components/reportFormat";

export default function ProductReports({ filters }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    ReportsService.products(filters)
      .then((r) => alive && setData(r.data))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [filters]);

  const rows = data?.rows || [];
  const stats = useMemo(() => {
    const soldQty = rows.reduce((s, r) => s + Number(r.sold_qty || 0), 0);
    const sales = rows.reduce((s, r) => s + Number(r.sales_amount || 0), 0);
    const purchaseCost = rows.reduce((s, r) => s + Number(r.purchase_cost || 0), 0);
    const profit = rows.reduce((s, r) => s + Number(r.actual_profit || 0), 0);
    const expired = rows.reduce((s, r) => s + Number(r.expired_batches || 0), 0);
    const reviews = rows.reduce((s, r) => s + Number(r.total_reviews || 0), 0);
    return { total: rows.length, soldQty, sales, purchaseCost, profit, expired, reviews };
  }, [rows]);

  const mostSold = [...rows].sort((a, b) => Number(b.sold_qty || 0) - Number(a.sold_qty || 0)).slice(0, 6);
  const leastSold = [...rows].sort((a, b) => Number(a.sold_qty || 0) - Number(b.sold_qty || 0)).slice(0, 6);
  const trendRows = mostSold.map((r, i) => ({ bucket: r.next_expiry || new Date(Date.now() + i * 86400000).toISOString(), sales_amount: Number(r.sales_amount || 0), name: r.name }));

  if (loading) return <div className="py-14 text-center font-bold text-[#d86a78]">Loading product report...</div>;

  return (
    <div className="space-y-5">
      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
        <ReportCard icon={Boxes} title="Products" value={n(stats.total)} />
        <ReportCard icon={PackageCheck} title="Sold Qty" value={n(stats.soldQty)} />
        <ReportCard icon={BadgeIndianRupee} title="Sales" value={money(stats.sales)} />
        <ReportCard icon={BadgeIndianRupee} title="Purchase Cost" value={money(stats.purchaseCost)} />
        <ReportCard icon={Tags} title="Profit" value={money(stats.profit)} />
        <ReportCard icon={PackageX} title="Expired Batches" value={n(stats.expired)} />
        <ReportCard icon={Star} title="Reviews" value={n(stats.reviews)} />
      </div>

      <TrendBars rows={trendRows} field="sales_amount" label="Product sales trend" subtitle="Graph is created from top product sales when API trend is not available" />

      <div className="grid gap-5 xl:grid-cols-2">
        <ReportPanel title="Most Sold Products">
          <DataTable rows={mostSold} columns={["name", "sold_qty", "sales_amount", "actual_profit"]} maxHeight="max-h-[300px]" />
        </ReportPanel>
        <ReportPanel title="Least Sold Products">
          <DataTable rows={leastSold} columns={["name", "sold_qty", "sales_amount", "actual_profit"]} maxHeight="max-h-[300px]" />
        </ReportPanel>
      </div>

      <ReportPanel>
        <DataTable
          rows={rows}
          columns={["name","sku","status","variant_count","sold_qty","sales_amount","purchase_cost","actual_profit","profit_per_unit","avg_purchase_price","profit_margin_percent","missing_purchase_price_items","order_count","qty_available","expired_batches","next_expiry","avg_rating","total_reviews","mrp","selling_price"]}
        />
      </ReportPanel>
    </div>
  );
}
